import React, { useState } from 'react';
import { useLanguage } from '../LanguageContext';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, Play } from 'lucide-react';
export const Hero = () => {
  const {
    t
  } = useLanguage();
  const [showVideo, setShowVideo] = useState(false);
  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-slate-50" data-id="element-75">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none" data-id="element-76">
        <div className="absolute top-20 right-0 w-96 h-96 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob" data-id="element-77"></div>
        <div className="absolute top-40 -left-20 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000" data-id="element-78"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-79">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-80">
          {/* Text Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-2xl" data-id="element-81">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 font-medium text-sm mb-6" data-id="element-82">
              <span className="w-2 h-2 rounded-full bg-indigo-600 animate-pulse" data-id="element-83"></span>
              {t.hero.trustSignals[0]}
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight mb-6 tracking-tight" data-id="element-84">
              {t.hero.headline}
            </h1>

            <p className="text-lg sm:text-xl text-slate-600 mb-8 leading-relaxed" data-id="element-85">
              {t.hero.subhead}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10" data-id="element-86">
              <button onClick={() => scrollTo('booking')} className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all shadow-lg shadow-indigo-200 hover:shadow-xl transform hover:-translate-y-1 flex items-center justify-center gap-2" data-id="element-87">
                {t.hero.cta}
                <ArrowRight size={20} data-id="element-88" />
              </button>
            </div>

            <div className="flex flex-wrap gap-y-3 gap-x-6" data-id="element-89">
              {t.hero.trustSignals.map((signal, idx) => <div key={idx} className="flex items-center gap-2 text-slate-600 font-medium text-sm" data-id="element-90">
                  <CheckCircle2 size={18} className="text-emerald-500" data-id="element-91" />
                  {signal}
                </div>)}
            </div>
          </motion.div>

          {/* Image Content */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative w-full lg:ml-auto" data-id="element-92">
            <div className="relative w-full rounded-3xl overflow-hidden shadow-2xl aspect-video max-w-xl mx-auto lg:mr-0 border-8 border-white bg-slate-900" data-id="element-93">
              {showVideo ? <iframe className="absolute inset-0 w-full h-full" src="https://www.youtube.com/embed/5seR8QWqXmM?autoplay=1&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3" title="Mora Rebori - Introduction" frameBorder="0" referrerPolicy="strict-origin-when-cross-origin" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen data-id="element-94" /> : <button type="button" onClick={() => setShowVideo(true)} className="group absolute inset-0 w-full h-full flex items-center justify-center" aria-label="Play introduction video" data-id="element-95">
                  <img src="https://i.ytimg.com/vi/5seR8QWqXmM/maxresdefault.jpg" alt="Mora Rebori introduction video thumbnail" width={1280} height={720} loading="eager" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-96" />
                  <div className="absolute inset-0 bg-slate-900/20 group-hover:bg-slate-900/10 transition-colors" data-id="element-97" />
                  <span className="relative w-20 h-20 bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center transition-transform group-hover:scale-110" data-id="element-98">
                    <span className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg" data-id="element-99">
                      <Play size={28} className="text-indigo-600 ml-1" fill="currentColor" data-id="element-100" />
                    </span>
                  </span>
                </button>}
            </div>

            {/* Decorative dots */}
            <div className="absolute -z-10 -bottom-6 -right-6 w-32 h-32 text-indigo-200" data-id="element-101">
              <svg viewBox="0 0 100 100" fill="currentColor" data-id="element-102">
                <pattern id="dots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse" data-id="element-103">
                  <circle cx="2" cy="2" r="2" data-id="element-104" />
                </pattern>
                <rect width="100" height="100" fill="url(#dots)" data-id="element-105" />
              </svg>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
};